import type { Payload, RelayToDevice } from '@relyo/protocol';
import { runClick, runInput, runKey, runScroll, type RejectReason } from './actions';
import type { RedactionConfig } from './redaction';

/**
 * Remote Control (Spec Kap. 5 Punkt 7, Kap. 9): zweiter Consent, Ausführung der Befehle und
 * Nutzer-Interrupt. Zustand wie im Protokoll: `none → requested → granted → none`.
 */

export type ControlState = 'none' | 'requested' | 'granted';

export type ControlCommand = Extract<
  RelayToDevice,
  {
    t:
      | 'control.click'
      | 'control.input'
      | 'control.scroll'
      | 'control.key'
      | 'control.tap'
      | 'control.drag'
      | 'control.text';
  }
>;

export type ControlReply =
  | { t: 'control.granted' | 'control.denied' }
  | { t: 'control.revoked'; reason: string }
  | { t: 'control.rejected'; cmdId: string; reason: RejectReason };

export interface ControlHost {
  /** Schickt die Antwort an den Relay (der Aufrufer ergänzt `sessionId`). */
  reply(msg: ControlReply): void;
  /** rrweb-Mirror: nodeId → DOM-Knoten. */
  getNode(id: number): Node | null;
  /** Ereignis bzw. Knoten gehört zur SDK-Oberfläche. */
  isOwnEvent(ev: Event): boolean;
  isOwnNode(n: Node): boolean;
  /** Anzeige aktualisieren (Modal, Statusleiste). */
  render(state: ControlState, agentName: string): void;
}

export class RemoteControl {
  private stateValue: ControlState = 'none';
  private agentName = '';
  private listening = false;

  constructor(
    private readonly host: ControlHost,
    private readonly redaction: RedactionConfig,
  ) {}

  get state(): ControlState {
    return this.stateValue;
  }

  /** `control.request`: zweites Consent-Modal zeigen. */
  request(agentName: string): void {
    if (this.stateValue === 'granted') return;
    this.agentName = agentName;
    this.set('requested');
  }

  /** Antwort des Nutzers auf die Steuerungsanfrage. */
  answer(granted: boolean): void {
    if (this.stateValue !== 'requested') return;
    if (granted) {
      this.set('granted');
      this.host.reply({ t: 'control.granted' });
    } else {
      this.set('none');
      this.host.reply({ t: 'control.denied' });
    }
  }

  /** „Steuerung beenden“ in der Statusleiste. */
  stop(): void {
    this.revoke('user_stop');
  }

  /** `control.end`, Session-Ende, Verbindungsverlust: lokal beenden, nichts senden. */
  reset(): void {
    if (this.stateValue === 'none') return;
    this.set('none');
  }

  /** Führt einen Steuerbefehl aus oder lehnt ihn ab. */
  execute(cmd: ControlCommand): void {
    const reason = this.run(cmd);
    if (reason) this.host.reply({ t: 'control.rejected', cmdId: cmd.p.cmdId, reason });
  }

  // ----------------------------------------------------------------- intern

  private run(cmd: ControlCommand): RejectReason | null {
    if (this.stateValue !== 'granted') return 'not_granted';
    try {
      switch (cmd.t) {
        case 'control.click':
          return this.guard(cmd.p.nodeId, (n) => runClick(n));
        case 'control.input':
          return this.guard(cmd.p.nodeId, (n) =>
            typeof cmd.p.value === 'string'
              ? runInput(n, cmd.p.value, this.redaction)
              : 'unsupported',
          );
        case 'control.scroll': {
          const p: Payload<RelayToDevice, 'control.scroll'> = cmd.p;
          if (p.nodeId === undefined || p.nodeId === null) return runScroll(undefined, p.x, p.y);
          return this.guard(p.nodeId, (n) => runScroll(n, p.x, p.y));
        }
        case 'control.key':
          return runKey(cmd.p.key, cmd.p.code, cmd.p.modifiers ?? {}, (n) =>
            this.host.isOwnNode(n),
          );
        default:
          // tap/drag/text sind Flutter-Befehle.
          return 'unsupported';
      }
    } catch {
      return 'unsupported';
    }
  }

  /** Knoten auflösen; die SDK-Oberfläche selbst darf der Agent nicht bedienen. */
  private guard(nodeId: unknown, fn: (n: Node | null) => RejectReason | null): RejectReason | null {
    if (typeof nodeId !== 'number') return 'not_found';
    const node = this.host.getNode(nodeId);
    if (node && this.host.isOwnNode(node)) return 'forbidden';
    return fn(node);
  }

  private revoke(reason: 'user_stop' | 'user_interrupt'): void {
    if (this.stateValue !== 'granted') return;
    this.set('none');
    this.host.reply({ t: 'control.revoked', reason });
  }

  private set(state: ControlState): void {
    this.stateValue = state;
    this.listen(state === 'granted');
    this.host.render(state, this.agentName);
  }

  /**
   * Echte Nutzereingaben (`isTrusted`) widerrufen die Steuerung sofort. Capture-Listener auf
   * `window`, damit die Host-App das Ereignis nicht vorher abfangen kann. Eingaben in der
   * SDK-Oberfläche (z. B. „Steuerung beenden“) zählen nicht und führen ihre eigene Aktion aus.
   */
  private readonly onUserInput = (ev: Event): void => {
    if (this.stateValue !== 'granted' || ev.isTrusted !== true) return;
    if (this.host.isOwnEvent(ev)) return;
    this.revoke('user_interrupt');
  };

  private listen(on: boolean): void {
    if (on === this.listening) return;
    this.listening = on;
    if (on) {
      window.addEventListener('pointerdown', this.onUserInput, true);
      window.addEventListener('keydown', this.onUserInput, true);
    } else {
      window.removeEventListener('pointerdown', this.onUserInput, true);
      window.removeEventListener('keydown', this.onUserInput, true);
    }
  }
}
